/**
 * Smoothing the jump the camera takes when you walk up a step.
 *
 * "При переході гравця на блок вище він ніби телепортується на блок вище."
 * And so it did: walking onto a ledge is a step, the body is lifted the whole
 * block inside one frame, and a camera bolted to the eye goes with it. The
 * body is not wrong - it has to be up there before anything else asks where
 * it is - but the picture should not snap.
 *
 * So the camera is allowed to lag behind the eye and catch up. What is kept
 * is how far behind it still is; every frame the body rises in one go adds
 * to that, and every frame it closes at a fixed speed.
 *
 * Pure arithmetic over a number, so it can be argued with in a test. The
 * renderer keeps the number and subtracts it from the eye's height; nothing
 * here knows what a camera is.
 */

/**
 * The biggest single rise that counts as a step rather than as flying.
 *
 * A little over the step height, because that is what a step is. A jump
 * rises about fifteen hundredths of a block in a frame and would otherwise
 * be smoothed too - but a jump is *supposed* to look like leaving the
 * ground, and `grounded` settles that anyway.
 */
export const STEP_MAX = 1.2;

/** Rises smaller than this are the ordinary business of walking on hills. */
export const STEP_MIN = 0.02;

/** How fast the camera closes the gap, in blocks a second. */
export const STEP_CATCHUP = 9;

/**
 * How far the camera is behind the eye after this frame.
 *
 * @param lag where it was behind before, never negative
 * @param rise how far the eye went up this frame, which may be negative
 * @param grounded whether the feet were down: a step is a walking move, and
 *   in the air a rise is a jump and should be seen as one
 */
export function easeStep(
  lag: number, rise: number, grounded: boolean, dt: number,
): number {
  let out = lag;
  if (grounded && rise > STEP_MIN && rise <= STEP_MAX) out += rise;
  out -= STEP_CATCHUP * dt;
  // Never past the eye: the camera catches up and stops, it does not
  // overshoot into the floor.
  return out < 0 ? 0 : Math.min(out, STEP_MAX);
}
